import {
  DynamicData,
  DynamicField,
  DynamicFieldOutput
} from "@/types/dynamic-data";
import capitalize from "lodash/capitalize";
import defensiveCopy from "@/util/defensive-copy";
import { DynamicFieldReturnTypeV } from "@/types/dynamic-property-path";
import { isObject } from "@/util/objects";
import { getDynamicShortcodeHash } from "@/lib/dynamic-data/load";
import { pipe } from "fp-ts/function";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { fold } from "fp-ts/Either";

const SINGLE_DYNAMIC_SHORTCODE_REGEX =
  /^\[breakdance_dynamic(?:\s+[a-zA-Z0-9_-]+=(?:"[^"]*"|'[^']*'))*\]$/;
const DYNAMIC_SHORTCODE_REGEX = /\[breakdance_dynamic\b(?:[^[\]]|\[.*?\])*\]/g;

export function isDynamicShortcode(value: unknown): value is string {
  // This checks if the string is exactly one breakdance_dynamic shortcode with no additional content.
  return (
    typeof value === "string" && SINGLE_DYNAMIC_SHORTCODE_REGEX.test(value)
  );
}

export function containsBreakdanceDynamicShortcode(
  value: unknown
): value is string {
  const regex = /\[breakdance_dynamic[^\]]*\]/;
  return typeof value === "string" && regex.test(value);
}

export function extractBreakdanceDynamicShortcodes(value: unknown): string[] {
  if (typeof value !== "string") return [];

  const matches = [];
  let match;

  while ((match = DYNAMIC_SHORTCODE_REGEX.exec(value)) !== null) {
    matches.push(match[0]);
  }

  return matches;
}

export function renderDynamicShortcodes(
  value: string,
  dynamicData: DynamicData,
  hasPreview = false
) {
  // Bail out early if no shortcodes are found.
  if (!containsBreakdanceDynamicShortcode(value) && !hasPreview) return value;

  // Handle exactly one shortcode separately (this could be an object)
  if (dynamicData[getDynamicShortcodeHash(value)]) {
    return dynamicData[getDynamicShortcodeHash(value)];
  }

  // Replace multiple shortcodes in a string with their value
  return value.replace(DYNAMIC_SHORTCODE_REGEX, match => {
    const resolvedValue = dynamicData[getDynamicShortcodeHash(match)];
    return resolvedValue || match; // Replace if key exists, else keep the original
  });
}

export function getDynamicMetaPath(path: string) {
  return `${path}_dynamic_meta`;
}

export function getFieldAndAttributesFromDynamicShortcode(shortcode: string) {
  const attributes: { [key: string]: string | undefined } = {};

  // https://stackoverflow.com/a/37577170/5993042
  shortcode.match(/[\w-]+='.*?'/g)?.forEach(function (attribute) {
    const attributeMatch = attribute.match(/([\w-]+)='(.*?)'/);

    if (attributeMatch) {
      const [, key, value] = attributeMatch;

      attributes[key] = value;
    }
  });

  // Explicitly separate field and attributes because even tho they're both shortcode attributes, we use them differently
  const field = defensiveCopy(attributes.field);
  delete attributes.field;

  return {
    field,
    attributes: attributes || {}
  };
}

export function getDynamicFieldByShortcode(
  fields: DynamicField[],
  shortcode: string
): DynamicField | undefined {
  const { field: slug } = getFieldAndAttributesFromDynamicShortcode(shortcode);

  return slug ? fields.find(field => field.slug === slug) : undefined;
}

/**
 * Converts "content.repeater_1[5].text" into "content.repeater_1[].text"
 */
export function getPathWithoutRepeaterIndex(path: string) {
  return path.replace(
    // any number inside brackets
    /\[[0-9]+\]/g,
    "[]"
  );
}

export function getDynamicPathAcceptsItems() {
  return DynamicFieldReturnTypeV.types.map(accepts => ({
    text: capitalize(accepts.value.replace("_", " ")),
    value: accepts.value
  }));
}

export function getFlattenedAttributesFromDynamicMetaData(
  attributes: object,
  field: DynamicField
) {
  const { defaultAttributes } = field;
  const atts = { ...defaultAttributes, ...attributes };

  const flattenedAttributes: Record<string, string | number> = {};

  Object.keys(atts).forEach(key => {
    const attributeKey = key as keyof typeof atts;
    const value = atts[attributeKey];

    if (value === null) return;

    if (typeof value === "object") {
      Object.entries(value).forEach(([childKey, childValue]) => {
        if (childValue === null) return;

        if (
          isObject(childValue) &&
          objectHasId(childValue) &&
          childValue.id !== undefined
        ) {
          flattenedAttributes[childKey.toLowerCase()] = childValue.id;
        } else if (
          typeof childValue === "string" ||
          typeof childValue === "number"
        ) {
          flattenedAttributes[childKey.toLowerCase()] = childValue
            .toString()
            .replace(/'/g, "&apos;");
        }
      });
    } else {
      flattenedAttributes[key] = value;
    }
  });

  return flattenedAttributes;
}

export function getShortcodeFromDynamicField(
  attributes: object,
  field: DynamicField
) {
  const flattenedAttributes = getFlattenedAttributesFromDynamicMetaData(
    attributes,
    field
  );
  const containsAttributes = Object.keys(flattenedAttributes).length;

  const attsJson = pipe(
    fpJsonStringify(flattenedAttributes),
    fold(
      () => "",
      result => result
    )
  )
    .replace(/\\"/g, `\\\\"`) // Escape double quotes twice
    .replace(/\\'/g, `\\\\'`); // Escape single quotes twice

  const params = containsAttributes ? ` params='${attsJson}'` : "";

  // Important: parseDynamicShortcode requires single quotes for attributes
  return `[breakdance_dynamic field='${field.slug}'${params}]`;
}

function objectHasId(object: unknown): object is { id: string } {
  return isObject(object) && "id" in object;
}

export function getSlugFromDynamicFieldOutput(
  dynamicField: DynamicFieldOutput
) {
  return typeof dynamicField.field === "string"
    ? dynamicField.field
    : dynamicField.field.slug;
}
